import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Collapse,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { debtScheduleAPI } from '../api';

const DEBT_TYPES = [
  { value: 'term_loan', labelKey: 'debtSchedule.debtTypes.term_loan' },
  { value: 'line_of_credit', labelKey: 'debtSchedule.debtTypes.line_of_credit' },
  { value: 'credit_card', labelKey: 'debtSchedule.debtTypes.credit_card' },
  { value: 'merchant_cash_advance', labelKey: 'debtSchedule.debtTypes.merchant_cash_advance' },
  { value: 'equipment_financing', labelKey: 'debtSchedule.debtTypes.equipment_financing' },
  { value: 'real_estate', labelKey: 'debtSchedule.debtTypes.real_estate' },
  { value: 'vehicle', labelKey: 'debtSchedule.debtTypes.vehicle' },
  { value: 'personal_loan', labelKey: 'debtSchedule.debtTypes.personal_loan' },
  { value: 'other', labelKey: 'debtSchedule.debtTypes.other' },
];

const EMPTY_DEBT = {
  creditorName: '',
  accountNumber: '',
  debtType: '',
  originalAmount: '',
  currentBalance: '',
  monthlyPayment: '',
  interestRate: '',
  maturityDate: '',
  collateral: '',
  isCurrent: true,
  notes: '',
};

const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString();
};

const getDebtLabel = (val, t) => {
  const found = DEBT_TYPES.find((dt) => dt.value === val);
  return found ? t(found.labelKey) : val;
};

const statusColors = {
  draft: 'default',
  submitted: 'primary',
  under_review: 'warning',
  approved: 'success',
  rejected: 'error',
};

// ────────────────────────────────────────────────────────────────────────────
// Mobile Card for a single debt row
// ────────────────────────────────────────────────────────────────────────────
const DebtCard = ({ debt, index, onEdit, onRemove, readOnly }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: '8px !important', pt: 1.5, px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
            <Typography variant="subtitle2" fontWeight={600}>
              {debt.creditorName || `Debt #${index + 1}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getDebtLabel(debt.debtType, t)} &middot; {t('debtSchedule.balance')}: {formatCurrency(debt.currentBalance)} &middot; {t('debtSchedule.payment')}: {formatCurrency(debt.monthlyPayment)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={open}>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={0.5}>
            <Row label={t('debtSchedule.originalAmount')} value={formatCurrency(debt.originalAmount)} />
            <Row label={t('debtSchedule.interestRate')} value={debt.interestRate ? `${debt.interestRate}%` : '—'} />
            <Row label={t('debtSchedule.maturityDate')} value={debt.maturityDate ? formatDate(debt.maturityDate) : '—'} />
            <Row label={t('debtSchedule.collateral')} value={debt.collateral || '—'} />
            <Row label={t('debtSchedule.currentQuestion')} value={debt.isCurrent ? t('debtSchedule.yes') : t('debtSchedule.no')} />
            {debt.notes && <Row label={t('debtSchedule.notesLabel')} value={debt.notes} />}
          </Stack>
          {!readOnly && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(index)}>{t('debtSchedule.edit')}</Button>
              <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onRemove(index)}>{t('debtSchedule.remove')}</Button>
            </Stack>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

// ────────────────────────────────────────────────────────────────────────────
// Add / Edit Debt Dialog
// ────────────────────────────────────────────────────────────────────────────
const DebtDialog = ({ open, onClose, onSave, initialData }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_DEBT);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initialData ? { ...EMPTY_DEBT, ...initialData, maturityDate: initialData.maturityDate ? initialData.maturityDate.substring(0, 10) : '' } : { ...EMPTY_DEBT });
      setErrors({});
    }
  }, [open, initialData]);

  const handleChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.creditorName.trim()) errs.creditorName = t('debtSchedule.required');
    if (!form.debtType) errs.debtType = t('debtSchedule.required');
    if (!form.originalAmount || parseFloat(form.originalAmount) <= 0) errs.originalAmount = t('debtSchedule.mustBePositive');
    if (!form.currentBalance && form.currentBalance !== 0) errs.currentBalance = t('debtSchedule.required');
    if (!form.monthlyPayment && form.monthlyPayment !== 0) errs.monthlyPayment = t('debtSchedule.required');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      ...form,
      originalAmount: parseFloat(form.originalAmount) || 0,
      currentBalance: parseFloat(form.currentBalance) || 0,
      monthlyPayment: parseFloat(form.monthlyPayment) || 0,
      interestRate: form.interestRate ? parseFloat(form.interestRate) : undefined,
      maturityDate: form.maturityDate || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? t('debtSchedule.editDebt') : t('debtSchedule.addDebtTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('debtSchedule.creditorLabel')}
            value={form.creditorName}
            onChange={handleChange('creditorName')}
            error={!!errors.creditorName}
            helperText={errors.creditorName}
            fullWidth
            required
          />
          <TextField
            label={t('debtSchedule.accountNumber')}
            value={form.accountNumber}
            onChange={handleChange('accountNumber')}
            fullWidth
          />
          <FormControl fullWidth required error={!!errors.debtType}>
            <InputLabel>{t('debtSchedule.typeOfDebtLabel')}</InputLabel>
            <Select value={form.debtType} label={t('debtSchedule.typeOfDebtLabel')} onChange={handleChange('debtType')}>
              {DEBT_TYPES.map((dt) => (
                <MenuItem key={dt.value} value={dt.value}>{t(dt.labelKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('debtSchedule.originalAmount')}
              type="number"
              value={form.originalAmount}
              onChange={handleChange('originalAmount')}
              error={!!errors.originalAmount}
              helperText={errors.originalAmount}
              fullWidth
              required
              InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
            />
            <TextField
              label={t('debtSchedule.currentBalance')}
              type="number"
              value={form.currentBalance}
              onChange={handleChange('currentBalance')}
              error={!!errors.currentBalance}
              helperText={errors.currentBalance}
              fullWidth
              required
              InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('debtSchedule.monthlyPaymentLabel')}
              type="number"
              value={form.monthlyPayment}
              onChange={handleChange('monthlyPayment')}
              error={!!errors.monthlyPayment}
              helperText={errors.monthlyPayment}
              fullWidth
              required
              InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
            />
            <TextField
              label={t('debtSchedule.interestRateLabel')}
              type="number"
              value={form.interestRate}
              onChange={handleChange('interestRate')}
              fullWidth
              InputProps={{ endAdornment: <Typography sx={{ ml: 0.5 }}>%</Typography> }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('debtSchedule.maturityDateLabel')}
              type="date"
              value={form.maturityDate}
              onChange={handleChange('maturityDate')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('debtSchedule.collateralLabel')}
              value={form.collateral}
              onChange={handleChange('collateral')}
              fullWidth
            />
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={form.isCurrent} onChange={handleChange('isCurrent')} />}
            label={t('debtSchedule.isCurrentLabel')}
          />
          <TextField
            label={t('debtSchedule.notesLabel')}
            value={form.notes}
            onChange={handleChange('notes')}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSave}>{initialData ? t('debtSchedule.update') : t('debtSchedule.addDebt')}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main Page Component
// ────────────────────────────────────────────────────────────────────────────
const DebtSchedule = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleId, setScheduleId] = useState(null);
  const [status, setStatus] = useState('draft');
  const [debts, setDebts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

  const readOnly = status !== 'draft';

  // ── Load existing schedule ──
  useEffect(() => {
    (async () => {
      try {
        const res = await debtScheduleAPI.getAll();
        const schedules = res.data.data || [];
        if (schedules.length > 0) {
          const latest = schedules[0]; // sorted by createdAt desc
          setScheduleId(latest._id);
          setStatus(latest.status);
          setDebts(latest.debts || []);
        }
      } catch (err) {
        console.error('Failed to load debt schedule:', err);
        toast.error(t('debtSchedule.toastLoadError', 'Failed to load debt schedule'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Totals ──
  const totalOriginal = debts.reduce((s, d) => s + (parseFloat(d.originalAmount) || 0), 0);
  const totalBalance = debts.reduce((s, d) => s + (parseFloat(d.currentBalance) || 0), 0);
  const totalPayment = debts.reduce((s, d) => s + (parseFloat(d.monthlyPayment) || 0), 0);

  // ── Add / Edit handlers ──
  const handleOpenAdd = () => { setEditIndex(null); setDialogOpen(true); };
  const handleOpenEdit = (idx) => { setEditIndex(idx); setDialogOpen(true); };

  const handleSaveDebt = (debtData) => {
    setDebts((prev) => {
      const next = [...prev];
      if (editIndex !== null) {
        next[editIndex] = debtData;
      } else {
        next.push(debtData);
      }
      return next;
    });
    setDialogOpen(false);
  };

  const handleRemove = (idx) => {
    setDebts((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Save draft ──
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await debtScheduleAPI.create({ debts });
      setScheduleId(res.data.data._id);
      setStatus(res.data.data.status);
      toast.success(t('debtSchedule.toastSaved', 'Debt schedule saved'));
    } catch (err) {
      console.error('Save error:', err);
      toast.error(t('debtSchedule.toastSaveError', 'Failed to save debt schedule'));
    } finally {
      setSaving(false);
    }
  };

  // ── Submit ──
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Save first, then submit
      let id = scheduleId;
      if (!id) {
        const saveRes = await debtScheduleAPI.create({ debts });
        id = saveRes.data.data._id;
        setScheduleId(id);
      } else {
        await debtScheduleAPI.create({ debts });
      }
      const res = await debtScheduleAPI.submit(id);
      setStatus(res.data.data.status);
      setConfirmSubmitOpen(false);
      toast.success(t('debtSchedule.toastSubmitted', 'Debt schedule submitted for review'));
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(t('debtSchedule.toastSubmitError', 'Failed to submit debt schedule'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ──
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            {t('debtSchedule.title', 'Outstanding Business Debt Schedule')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('debtSchedule.subtitle', 'List all outstanding debts associated with your business. Providing specific details helps accurately evaluate your application.')}
          </Typography>
        </Box>
        <Chip label={status.replace('_', ' ').toUpperCase()} color={statusColors[status] || 'default'} />
      </Box>

      {/* Submitted / Under Review Banner */}
      {readOnly && (
        <Alert severity={status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'info'} sx={{ mb: 3 }}>
          {status === 'submitted' && t('debtSchedule.statusSubmitted', 'Your debt schedule has been submitted and is awaiting review.')}
          {status === 'under_review' && t('debtSchedule.statusUnderReview', 'Your debt schedule is currently being reviewed.')}
          {status === 'approved' && t('debtSchedule.statusApproved', 'Your debt schedule has been approved.')}
          {status === 'rejected' && t('debtSchedule.statusRejected', 'Your debt schedule was returned for revisions. Please contact your loan officer.')}
        </Alert>
      )}

      {/* Summary Cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <SummaryCard title={t('debtSchedule.totalDebts', 'Total Debts')} value={debts.length} isCurrency={false} />
        <SummaryCard title={t('debtSchedule.totalOriginalAmount', 'Total Original Amount')} value={totalOriginal} />
        <SummaryCard title={t('debtSchedule.totalCurrentBalance', 'Total Current Balance')} value={totalBalance} />
        <SummaryCard title={t('debtSchedule.totalMonthlyPayment', 'Total Monthly Payment')} value={totalPayment} />
      </Stack>

      {/* Debts Table (desktop) / Cards (mobile) */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('debtSchedule.debts', 'Debts')}</Typography>
            {!readOnly && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                {t('debtSchedule.addDebt', 'Add Debt')}
              </Button>
            )}
          </Box>

          {debts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <ReceiptIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>{t('debtSchedule.noDebts', 'No debts added yet.')}</Typography>
              {!readOnly && (
                <Button sx={{ mt: 2 }} variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                  {t('debtSchedule.addFirstDebt', 'Add Your First Debt')}
                </Button>
              )}
            </Box>
          ) : isMobile ? (
            /* Mobile: Collapsible cards */
            debts.map((debt, i) => (
              <DebtCard key={i} debt={debt} index={i} onEdit={handleOpenEdit} onRemove={handleRemove} readOnly={readOnly} />
            ))
          ) : (
            /* Desktop: Table */
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('debtSchedule.creditor', 'Creditor / Lender')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('debtSchedule.debtType', 'Type of Debt')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">{t('debtSchedule.originalAmount', 'Original Amount')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">{t('debtSchedule.currentBalance', 'Current Balance')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">{t('debtSchedule.interestRate', 'Interest Rate')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">{t('debtSchedule.monthlyPayment', 'Monthly Payment')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('debtSchedule.maturityDate', 'Maturity Date')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('debtSchedule.collateral', 'Collateral')}</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="center">{t('debtSchedule.isCurrent', 'Current?')}</TableCell>
                    {!readOnly && <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="center">{t('debtSchedule.actions', 'Actions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debts.map((debt, i) => (
                    <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{debt.creditorName}</Typography>
                        {debt.accountNumber && <Typography variant="caption" color="text.secondary">#{debt.accountNumber}</Typography>}
                      </TableCell>
                      <TableCell>{getDebtLabel(debt.debtType, t)}</TableCell>
                      <TableCell align="right">{formatCurrency(debt.originalAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(debt.currentBalance)}</TableCell>
                      <TableCell align="right">{debt.interestRate ? `${debt.interestRate}%` : '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(debt.monthlyPayment)}</TableCell>
                      <TableCell>{debt.maturityDate ? formatDate(debt.maturityDate) : '—'}</TableCell>
                      <TableCell>{debt.collateral || '—'}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={debt.isCurrent ? t('debtSchedule.yes', 'Yes') : t('debtSchedule.no', 'No')} color={debt.isCurrent ? 'success' : 'error'} variant="outlined" />
                      </TableCell>
                      {!readOnly && (
                        <TableCell align="center">
                          <Tooltip title={t('debtSchedule.edit', 'Edit')}>
                            <IconButton size="small" onClick={() => handleOpenEdit(i)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title={t('debtSchedule.remove', 'Remove')}>
                            <IconButton size="small" color="error" onClick={() => handleRemove(i)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={2}><Typography variant="subtitle2" fontWeight={700}>{t('debtSchedule.totals', 'Totals')}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle2" fontWeight={700}>{formatCurrency(totalOriginal)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle2" fontWeight={700}>{formatCurrency(totalBalance)}</Typography></TableCell>
                    <TableCell />
                    <TableCell align="right"><Typography variant="subtitle2" fontWeight={700}>{formatCurrency(totalPayment)}</Typography></TableCell>
                    <TableCell colSpan={readOnly ? 3 : 4} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Action Bar */}
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || debts.length === 0}
          >
            {t('debtSchedule.saveDraft', 'Save Draft')}
          </Button>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            onClick={() => setConfirmSubmitOpen(true)}
            disabled={submitting || debts.length === 0}
          >
            {t('debtSchedule.submitForReview', 'Submit for Review')}
          </Button>
        </Box>
      )}

      {/* Disclaimer */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        {t('debtSchedule.disclaimer', 'By submitting this Outstanding Business Debts form, you affirm that: (i) you answered all items in this loan application fully, accurately and completely; and (ii) you are authorized to submit this form on behalf of the person and/or business whose name(s) you have entered in this form.')}
      </Typography>

      {/* Add / Edit Dialog */}
      <DebtDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveDebt}
        initialData={editIndex !== null ? debts[editIndex] : null}
      />

      {/* Confirm Submit Dialog */}
      <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} maxWidth="xs">
        <DialogTitle>{t('debtSchedule.confirmSubmitTitle', 'Submit Debt Schedule?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('debtSchedule.confirmSubmitBody', {
              count: debts.length,
              total: formatCurrency(totalBalance),
              defaultValue: `You are about to submit ${debts.length} debt${debts.length !== 1 ? 's' : ''} totaling ${formatCurrency(totalBalance)} in current balance. Once submitted, you will not be able to make changes until review is complete.`
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmitOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? t('debtSchedule.submitting', 'Submitting...') : t('debtSchedule.confirmSubmit', 'Confirm & Submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Summary Card
// ────────────────────────────────────────────────────────────────────────────
const SummaryCard = ({ title, value, isCurrency = true }) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>{title}</Typography>
      <Typography variant="h6" fontWeight={700}>
        {isCurrency ? formatCurrency(value) : value}
      </Typography>
    </CardContent>
  </Card>
);

export default DebtSchedule;
